import {
  EventSourceMessage,
  fetchEventSource,
} from "@microsoft/fetch-event-source";
import { axiosClient } from "./axiosClient";
import { setJWTHeader } from "./common";

export const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_HOST;
export const BACKEND_BASE_URL_TWITTER = process.env.REACT_APP_BACKEND_HOST_TWITTER;

export const searchSessions = async (text: string) => {
  try {
    let res = await axiosClient.post("/search", {
      text: text,
    });
    return res;
  } catch (error: any) {
    return error.response;
  }
};

export const generateSummary = (model: string) => {
  return axiosClient.post("/generate-summary", { model: model });
}

export const fetchSessionData = () => {
  return axiosClient.get("/get-session");
};

export const fetchUserSessions = () => {
  if (axiosClient.isAuthenticated()) {
    return axiosClient.get("/get-user-sessions");
  }
};

export const createSession = () => {
  return axiosClient.post('/create-session', {});
}

export const generateImage = (prompt: string, variations: number, model: string) => {
  return axiosClient.post("/generate-image", { prompt, variations, model });
};

export const fetchTextMessage = async (
  selectedType: string,
  session: string,
  messages: { role: string, content: string }[],
  model: string,
  onopen: (res: Response) => void,
  onmessage: (event: EventSourceMessage) => void,
  onerror: (err: any) => void,
  onclose: () => void,
  signal: AbortSignal
)=> {
  if(selectedType === 'text'){
    fetchMainTextMessage(
      session!,
      messages,
      model,
      onopen,
      onmessage,
      onerror,
      onclose,
      signal
    )
  }else{
    fetchAnalyseTweetsSummaryMessage(
      session!,
      messages,
      model,
      onopen,
      onmessage,
      onerror,
      onclose,
      signal
    )
  }

}

export const fetchMainTextMessage = async (
  session: string,
  messages: { role: string; content: string }[],
  model: string,
  onopen: (res: Response) => void,
  onmessage: (event: EventSourceMessage) => void,
  onerror: (err: any) => void,
  onclose: () => void,
  signal: AbortSignal
) => {
  const user = localStorage.getItem("user");

  await fetchEventSource(`${BACKEND_BASE_URL}/generate-text`, {
    openWhenHidden: true,
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      user: user!,
      session: session,
      Authorization: localStorage.getItem("access_token")!,
    },
    body: JSON.stringify({ messages, model }),
    // @ts-ignore
    onopen(res) {
      onopen(res);
    },
    onmessage(event) {
      onmessage(event);
    },
    onclose() {
      onclose();
    },
    onerror(err) {
      onerror(err);
    },
    signal,
  });
};

export const fetchAnalyseTweetsSummaryMessage = async (
  session: string,
  messages: { role: string, content: string }[],
  model: string,
  onopen: (res: Response) => void,
  onmessage: (event: EventSourceMessage) => void,
  onerror: (err: any) => void,
  onclose: () => void,
  signal: AbortSignal
) => {
  const user = localStorage.getItem('user');

  await fetchEventSource(`${BACKEND_BASE_URL_TWITTER}/analyse-tweets`, {
    openWhenHidden: true,
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      user: user!,
      session: session,
      Authorization: localStorage.getItem("access_token")!,
    },
    body: JSON.stringify({ messages, model }),
    // @ts-ignore
    onopen(res) {
      onopen(res);
    },
    onmessage(event) {
      onmessage(event);
    },
    onclose() {
      onclose();
    },
    onerror(err) {
      onerror(err);
    },
    signal,
  });
};

export const logout = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("user_info");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("expires_at");
  localStorage.removeItem("user");
  localStorage.removeItem("userId");
  axiosClient.removeHeader("Authorization");
  return axiosClient.post("/auth/logout", null);
};

export const verifyAuth = () => {
  return axiosClient.post("/auth/verify-token", null).catch(() => {
    localStorage.removeItem("access_token");
    setJWTHeader(true);
  });
};

export const refreshToken = async () => {
  try {
    const response = await axiosClient.post("/auth/refresh-token", {
      refresh_token: localStorage.getItem("refresh_token"),
    });
    const data: { [key: string]: string } = response.data.data;
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    localStorage.setItem("expires_at", data.expires_at);
    setJWTHeader();
    return response;
  } catch (error: any) {
    return error.response;
  }
};

export const emailLogin = async (values: {
  email: string;
  password: string;
}) => {
  try {
    const response = await axiosClient.post("/auth/login", values);
    const data: { [key: string]: string } = response.data.data;
    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    localStorage.setItem("expires_at", data.expires_at);
    setJWTHeader();

    // in 3599 seconds, refresh token
    setTimeout(() => {
      refreshToken();
    }, 3599 * 1000);

    return response;
  } catch (error: any) {
    return error.response;
  }
};

export const signUp = async (values: {
  email: string;
  password: string;
  confirm_password: string;
}) => {
  try {
    const res = await axiosClient.post("/auth/signup", values);
    return res;
  } catch (error: any) {
    return error.response;
  }
};

export const socialLogInAPI = async (bodyData: {
  email: object;
  email_verified: object;
  source_id: string;
  id_token: string;
}) => {
  try {
    const res = await axiosClient.post("/auth/social-login", bodyData);
    const data: { [key: string]: string } = res.data.data;

    localStorage.setItem("access_token", data.access_token);
    localStorage.setItem("refresh_token", data.refresh_token);
    localStorage.setItem("expires_at", data.expires_at);

    // in 3599 seconds, refresh token
    setTimeout(() => {
      refreshToken();
    }, 3599 * 1000);

    return res;
  } catch (error) {
    return error;
  }
};

export const forgetPassword = async (values: { email: string }) => {
  try {
    const res = await axiosClient.post("/auth/forgot-password", values);
    return res;
  } catch (error: any) {
    return error.response;
  }
};
